/**
 * Home map AI — FAB + device-head WARNING (tip 539).
 * Quiet = FAB · Watching · Alert = small WARNING above the device pin (not corner / not fullscreen).
 * Humans keep the gate; AI runs sense → explain → suggest continuously.
 */
import { escapeHtml } from "./dom.js";
import { softOperatorNote } from "./format.js";
import { decisionSurfaceActionsHtml } from "./decision-surface.js";
import { buildAiContextDeepHtml } from "./ai-context-deep.js";

/**
 * @param {number|null|undefined} score
 */
function softPriorityBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Priority · review";
  if (n >= 80) return "Critical · decide now";
  if (n >= 60) return "High · review soon";
  if (n >= 40) return "Elevated · watch";
  return "Low · monitor";
}

/**
 * @param {object|null|undefined} it
 */
function softPlanLabel(it) {
  const summary = softOperatorNote(
    String((it && (it.summary || it.title || it.recommendation || it.detail)) || ""),
  ).trim();
  if (summary && summary.length >= 4 && !/^(unknown|n\/?a|—|-)$/i.test(summary)) return summary;
  const st = String((it && it.status) || "").trim().toLowerCase();
  if (st === "pending" || st === "open") return "Needs You — the Team is waiting";
  return "Sentinel plan ready for review";
}

/**
 * Prefer structured reason_json / reasons so the pin card never shows empty "—".
 * @param {object|null|undefined} it
 */
function softPlanWhy(it) {
  const raw =
    (it && (it.reason_json || it.reasons || it.reason || it.why || it.model_reason || it.recommendation)) ||
    "";
  if (Array.isArray(raw)) {
    const parts = raw
      .map((x) => softOperatorNote(String(x || "")).trim())
      .filter((s) => s && s.length >= 3 && !/^(unknown|n\/?a|—|-)$/i.test(s));
    if (parts.length) return parts.slice(0, 3).join(" · ");
  }
  const one = softOperatorNote(String(raw || "")).trim();
  if (one && one.length >= 4 && !/^(unknown|n\/?a|—|-)$/i.test(one)) return one;
  return "Tap this board pin for full Assessment · rules already scored the site";
}

/**
 * @param {string} bodyHtml
 * @param {{ open?: boolean, alert?: boolean, label?: string }} [opts]
 */
function wrapAiModal(bodyHtml, opts = {}) {
  const open = !!opts.open;
  const alert = !!opts.alert;
  const label = opts.label || (alert ? "Sentinel AI alert" : "AI Context");
  // Alert = device-scoped map popover (not aria-modal fullscreen — map stays usable).
  const role = alert ? "alertdialog" : "dialog";
  return `
  <div class="croc-ai-modal${alert ? " croc-ai-modal--device" : ""}${open ? " is-open" : ""}" data-ai-modal="1" data-ai-modal-open="${open ? "1" : "0"}"${alert ? ' data-ai-modal-alert="1"' : ""} aria-hidden="${open ? "false" : "true"}">
    <button type="button" class="croc-ai-modal__backdrop" data-ai-modal-close="1" aria-label="Close AI popup" tabindex="-1"></button>
    <div class="croc-ai-modal__dialog glass-panel glass-panel--float${alert ? " croc-ai-modal__dialog--alert" : ""}" role="${role}" aria-modal="${alert ? "false" : "true"}" aria-label="${escapeHtml(label)}">
      <header class="croc-ai-modal__chrome">
        <p class="croc-ai-modal__eyebrow">${alert ? "Needs you · this board" : "AI Context · auto"}</p>
        <button type="button" class="btn sm ghost btn-tap croc-ai-modal__x" data-ai-modal-close="1" aria-label="Close">×</button>
      </header>
      <div class="croc-ai-modal__body croc-cc-scroll">${bodyHtml}</div>
    </div>
  </div>`;
}

/**
 * FAB + modal as siblings under host so WARNING can leave the corner and sit on a pin.
 * @param {string} fabInner
 * @param {string} modalHtml
 * @param {string} [fabClass]
 */
function stageShell(fabInner, modalHtml, fabClass = "") {
  const cls = fabClass ? ` ${fabClass}` : "";
  return `
    <div class="croc-ai-fab-wrap" data-ai-stage-wrap="1">
      <button type="button" class="croc-ai-fab${cls} glass-panel glass-panel--float btn-tap" data-ai-open-modal="1" aria-haspopup="dialog">
        ${fabInner}
      </button>
    </div>
    ${modalHtml}`;
}

/**
 * Lead waiting plan device id (for pin anchor).
 * @param {object|null|undefined} aiPack
 */
export function leadAiWarnDeviceId(aiPack) {
  const items = Array.isArray(aiPack && aiPack.items) ? aiPack.items : [];
  const lead = items.find((it) => {
    const st = String((it && it.status) || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
  return lead ? String(lead.device_id || "").trim() : "";
}

/**
 * Position WARNING above the device pin (map.project). Falls back to FAB corner.
 * @param {ParentNode|null|undefined} view
 * @param {import('maplibre-gl').Map|null|undefined} [mapIn]
 */
export function syncMapAiWarnAnchor(view, mapIn) {
  if (!view || !view.querySelector) return;
  const host = view.querySelector("[data-ai-stage-host]");
  const wrap = view.querySelector(".croc-cc-map-wrap");
  if (!host || !wrap) return;
  const modal = host.querySelector(".croc-ai-modal");
  if (!modal) return;
  const open = modal.classList.contains("is-open") || modal.getAttribute("data-ai-modal-open") === "1";
  const isAlert = modal.classList.contains("croc-ai-modal--device")
    || modal.getAttribute("data-ai-modal-alert") === "1";
  const map = mapIn || (typeof window !== "undefined" ? window.__crocCcMap : null);
  const lng = Number(host.getAttribute("data-ai-lng"));
  const lat = Number(host.getAttribute("data-ai-lat"));
  const canPin = !!(
    open
    && isAlert
    && map
    && typeof map.project === "function"
    && Number.isFinite(lng)
    && Number.isFinite(lat)
  );

  if (!canPin) {
    host.setAttribute("data-ai-anchor", "fab");
    modal.style.left = "";
    modal.style.top = "";
    modal.style.right = "";
    modal.style.bottom = "";
    modal.style.transform = "";
    return;
  }

  host.setAttribute("data-ai-anchor", "device");
  try {
    const pt = map.project([lng, lat]);
    const canvas = map.getContainer && map.getContainer();
    if (!canvas || !pt) {
      host.setAttribute("data-ai-anchor", "fab");
      return;
    }
    const wrapRect = wrap.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const mw = Math.min(300, Math.max(220, wrapRect.width - 28));
    const pad = 14;
    let x = canvasRect.left - wrapRect.left + pt.x;
    let y = canvasRect.top - wrapRect.top + pt.y;
    x = Math.max(pad + mw / 2, Math.min(wrapRect.width - pad - mw / 2, x));
    // Keep room for the popover above the pin (avoid top/bottom clip).
    y = Math.max(148, Math.min(wrapRect.height - pad, y));
    modal.style.left = `${Math.round(x)}px`;
    modal.style.top = `${Math.round(y)}px`;
    modal.style.right = "auto";
    modal.style.bottom = "auto";
    modal.style.transform = "translate(-50%, calc(-100% - 18px))";
    modal.style.width = `${Math.round(mw)}px`;
    modal.style.maxWidth = `${Math.round(mw)}px`;
  } catch (_) {
    host.setAttribute("data-ai-anchor", "fab");
  }
}

/**
 * @param {object|null|undefined} aiPack
 * @param {object[]} [incidentCards]
 * @param {{ preview?: boolean, isAdmin?: boolean, modalOpen?: boolean }} [opts]
 */
export function buildMapAiAlertStageHtml(aiPack = null, incidentCards = [], opts = {}) {
  const pack = aiPack && typeof aiPack === "object" ? aiPack : {};
  const aiOn = pack.aiOn !== false;
  const isAdmin = !!opts.isAdmin;
  const items = Array.isArray(pack.items) ? pack.items : [];
  const waiting = items.filter((it) => {
    const st = String((it && it.status) || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
  const alarms = Array.isArray(incidentCards) ? incidentCards : [];
  const preview = !!opts.preview;
  const lead = waiting[0] || null;
  const alarmLead = alarms[0] || null;
  const mode = !aiOn ? "off" : lead || preview ? "alert" : "watch";
  const forceModal = opts.modalOpen === true;

  if (mode === "off") {
    const body = `
      <aside class="croc-ai-stage" data-ai-stage="off" aria-label="Sentinel AI">
        <p class="croc-ai-stage__eyebrow">Sentinel AI · Soft</p>
        <h3 class="croc-ai-stage__title">Ready</h3>
        <p class="muted croc-ai-stage__lede">Server AI runs auto when policy is on. Trouble shows as a crown on that device pin — tap the device, not a floating card.</p>
        <div class="croc-ai-stage__acts">
          <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel">Policy</button>
        </div>
      </aside>`;
    return stageShell(
      `<span class="croc-ai-fab__pulse" aria-hidden="true"></span>
        <span class="croc-ai-fab__k">AI</span>
        <span class="croc-ai-fab__v">Ready</span>`,
      wrapAiModal(body, { open: forceModal, alert: false }),
    );
  }

  if (mode === "watch") {
    const liveN = alarms.length;
    const deep = buildAiContextDeepHtml(pack, { isAdmin });
    const body = `
      <aside class="croc-ai-stage" data-ai-stage="watch" aria-label="Sentinel AI watching">
        <div class="croc-ai-stage__head">
          <p class="croc-ai-stage__eyebrow">Sentinel AI · auto</p>
          <span class="badge online">Watching</span>
        </div>
        <h3 class="croc-ai-stage__title">Map large · pin crowns</h3>
        <div class="croc-ai-stage__logic" role="list">
          <div class="croc-ai-stage__row" role="listitem">
            <span class="croc-ai-stage__k">Auto</span>
            <span class="croc-ai-stage__v">Server sense → explain → suggest · each device keeps its own crown</span>
          </div>
          <div class="croc-ai-stage__row" role="listitem">
            <span class="croc-ai-stage__k">Fact</span>
            <span class="croc-ai-stage__v">${liveN > 0
              ? escapeHtml(`${liveN} live alarm${liveN === 1 ? "" : "s"} on the feed`)
              : "Devices quiet · notify still fires first when something lands"}</span>
          </div>
          <div class="croc-ai-stage__row" role="listitem">
            <span class="croc-ai-stage__k">Gate</span>
            <span class="croc-ai-stage__v">Needs you = crown on that unit · tap the device for a clear preview · no auto fullscreen card</span>
          </div>
        </div>
        <div class="croc-ai-stage__acts">
          <button type="button" class="btn sm btn-tap" data-ai-stage-preview="1" title="Soft preview of the alarm stage">Preview alert · Soft</button>
          <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel">Policy</button>
        </div>
        <p class="honest croc-ai-stage__note">Box detection and fabric reach not live · App GPS live on Home map (stale labeled) · FE never binds a vendor SDK.</p>
      </aside>
      <div class="croc-ai-modal__deep">${deep}</div>`;
    return stageShell(
      `<span class="croc-ai-fab__pulse" aria-hidden="true"></span>
        <span class="croc-ai-fab__k">AI · auto</span>
        <span class="croc-ai-fab__v">Watching</span>`,
      wrapAiModal(body, { open: forceModal, alert: false, label: "AI Context" }),
      "croc-ai-fab--live",
    );
  }

  // alert / preview — Decision popup on the map (open only when opts.modalOpen)
  const id = lead
    ? String(lead.incident_id || lead.id || lead.item_id || "")
    : alarmLead && alarmLead.eventId != null
      ? String(alarmLead.eventId)
      : "";
  const risk = lead && (lead.risk_score != null ? lead.risk_score : lead.risk != null ? lead.risk : null);
  const band = risk != null ? softPriorityBand(risk) : preview ? "Soft preview · not a live alarm" : "Priority · review";
  const cat = softOperatorNote(
    String((lead && (lead.category || lead.classification || lead.alarm_class)) || (alarmLead && alarmLead.title) || "Security review"),
  );
  const headline = lead
    ? softPlanLabel(lead)
    : alarmLead
      ? softOperatorNote(String(alarmLead.title || "Live alarm"))
      : "Alert stage · Soft preview";
  const reason = lead
    ? softPlanWhy(lead)
    : preview
      ? "The Watch would flag this site · model may write a short why · you still Continue or Hold."
      : "Tap the lit device pin for the full Assessment.";
  const stageLabel = preview && !lead ? "Alert stage · Soft" : "Needs you · Decide";
  const modalOpen = opts.modalOpen === true;
  const deviceLabel = lead
    ? softOperatorNote(String(lead.device_id || lead.device_label || "")).trim()
    : "";

  const body = `
  <aside class="croc-ai-stage croc-ai-stage--alert" data-ai-stage="alert"${preview && !lead ? ' data-ai-stage-preview-on="1"' : ""} aria-label="Sentinel AI alert">
    <p class="croc-ai-stage__watermark" aria-hidden="true">WARNING</p>
    <div class="croc-ai-stage__head">
      <p class="croc-ai-stage__eyebrow">${escapeHtml(stageLabel)}</p>
      <span class="badge warn">${escapeHtml(band)}</span>
    </div>
    <h3 class="croc-ai-stage__title">${escapeHtml(headline)}</h3>
    <p class="muted croc-ai-stage__meta">${escapeHtml(cat)}${risk != null ? ` · score ${escapeHtml(String(Math.round(Number(risk))))}` : ""}${deviceLabel ? ` · ${escapeHtml(deviceLabel)}` : ""}</p>
    <div class="croc-ai-stage__logic" role="list">
      <div class="croc-ai-stage__row" role="listitem">
        <span class="croc-ai-stage__k">Auto</span>
        <span class="croc-ai-stage__v">The Watch already assessed · Continue stays on the loop</span>
      </div>
      <div class="croc-ai-stage__row" role="listitem">
        <span class="croc-ai-stage__k">Fact</span>
        <span class="croc-ai-stage__v">${escapeHtml(cat)} · notify already fired first</span>
      </div>
      <div class="croc-ai-stage__row" role="listitem">
        <span class="croc-ai-stage__k">Why</span>
        <span class="croc-ai-stage__v">${escapeHtml(reason)}</span>
      </div>
    </div>
    <div class="croc-ai-stage__acts">
      ${lead && id
        ? `${decisionSurfaceActionsHtml({ incidentId: id, context: "home", compact: true, waiting: true })}
           <button type="button" class="btn sm ghost btn-tap" data-ai-map-focus="${escapeHtml(id)}" data-ai-map-tab="assessment">Show on map</button>`
        : `<button type="button" class="btn sm btn-tap" data-ai-stage-preview="0">Close preview</button>
           <button type="button" class="btn sm secondary btn-tap" data-cc-go="incidents">Open cases</button>`}
      <button type="button" class="btn sm ghost btn-tap" data-cc-go="auto-sentinel">Policy</button>
      <button type="button" class="btn sm ghost btn-tap" data-ai-stage-dismiss="1" title="Hide popup — plan stays open">Later</button>
    </div>
    <p class="honest croc-ai-stage__note">${preview && !lead
      ? "Soft preview only · no live alarm · box detection and fabric reach not live."
      : "Team interrupt above this board pin · Frame Soft · map stays open for other boards."}</p>
  </aside>`;

  return stageShell(
    `<span class="croc-ai-fab__pulse" aria-hidden="true"></span>
      <span class="croc-ai-fab__k">AI · gate</span>
      <span class="croc-ai-fab__v">Needs you</span>`,
    wrapAiModal(body, { open: modalOpen, alert: true, label: "Needs you · this board" }),
    "croc-ai-fab--alert",
  );
}

/**
 * @param {ParentNode} view
 * @param {object|null|undefined} aiPack
 * @param {object[]} [incidentCards]
 * @param {{ preview?: boolean, isAdmin?: boolean, modalOpen?: boolean }} [opts]
 */
export function patchMapAiAlertStage(view, aiPack, incidentCards = [], opts = {}) {
  if (!view) return;
  const host = view.querySelector("[data-ai-stage-host]");
  if (!host) return;
  const preview = opts.preview != null
    ? !!opts.preview
    : host.getAttribute("data-ai-preview") === "1";
  const pack = aiPack === undefined ? (view.__ccAiPack || null) : aiPack;
  const cards = Array.isArray(incidentCards) ? incidentCards : [];
  const waiting = Array.isArray(pack && pack.items)
    ? pack.items.filter((it) => {
      const st = String((it && it.status) || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    })
    : [];
  const waitingN = waiting.length;
  const leadId = waiting[0]
    ? String(waiting[0].incident_id || waiting[0].id || waiting[0].item_id || "")
    : "";
  const leadDevice = waiting[0] ? String(waiting[0].device_id || "").trim() : "";
  const snoozeId = host.getAttribute("data-ai-snooze-id") || "";
  const usePreview = preview && waitingN === 0;
  if (usePreview) host.setAttribute("data-ai-preview", "1");
  else host.removeAttribute("data-ai-preview");
  // New waiting plan clears prior snooze (FAB can reopen; pins stay crowned).
  if (leadId && snoozeId && snoozeId !== leadId) {
    host.removeAttribute("data-ai-snooze-id");
  }
  const isAdmin = opts.isAdmin != null
    ? !!opts.isAdmin
    : !!(view.querySelector && view.querySelector('[data-home-role="admin"]'));
  // Never auto-open the floating card — crowns live on device pins; operator taps a unit.
  let modalOpen;
  if (opts.modalOpen === true) modalOpen = true;
  else if (opts.modalOpen === false) modalOpen = false;
  else if (host.getAttribute("data-ai-modal-keep") === "1") modalOpen = true;
  else modalOpen = false;
  host.innerHTML = buildMapAiAlertStageHtml(pack, cards, {
    preview: usePreview,
    isAdmin,
    modalOpen,
  });
  if (leadDevice) host.setAttribute("data-ai-device-id", leadDevice);
  else host.removeAttribute("data-ai-device-id");
  if (leadId) host.setAttribute("data-ai-plan-id", leadId);
  else host.removeAttribute("data-ai-plan-id");
  const mapWrap = view.querySelector(".croc-cc-map-wrap");
  const alertOn = host.querySelector('[data-ai-stage="alert"]');
  const modal = host.querySelector("[data-ai-modal]");
  if (mapWrap) {
    mapWrap.setAttribute("data-ai-alert", alertOn ? "1" : "0");
    mapWrap.setAttribute("data-ai-modal", modal && modal.getAttribute("data-ai-modal-open") === "1" ? "1" : "0");
  }
  try {
    document.body.dataset.ccAiAlert = alertOn ? "1" : "0";
    document.body.dataset.ccAiModal = modal && modal.getAttribute("data-ai-modal-open") === "1" ? "1" : "0";
  } catch (_) {}
  try {
    syncMapAiWarnAnchor(view);
  } catch (_) {}
}

/**
 * @param {Element|null} host
 * @param {boolean} open
 */
export function setAiMapModalOpen(host, open) {
  if (!host) return;
  const modal = host.querySelector("[data-ai-modal]");
  if (!modal) return;
  modal.classList.toggle("is-open", !!open);
  modal.setAttribute("data-ai-modal-open", open ? "1" : "0");
  modal.setAttribute("aria-hidden", open ? "false" : "true");
  if (open) host.setAttribute("data-ai-modal-keep", "1");
  else host.removeAttribute("data-ai-modal-keep");
  const mapWrap = host.closest(".croc-cc-map-wrap");
  if (mapWrap) mapWrap.setAttribute("data-ai-modal", open ? "1" : "0");
  try {
    document.body.dataset.ccAiModal = open ? "1" : "0";
  } catch (_) {}
  try {
    const view = host.closest("#view") || host.closest(".croc-cc") || document;
    syncMapAiWarnAnchor(view);
  } catch (_) {}
}
